/**
 * Write paths for the six math exercise sub-forms (`Blocks/Assignments/Editor/Math/Exercises/*`).
 * Every sub-form takes the same one prop — the exercise — which is what makes one registry
 * dispatcher legitimate. Components never `v-model` onto the document: each write is a store action
 * addressed by the exercise id, and reads are guarded (stored `data` is unvalidated).
 */
import { computed } from 'vue'

import type { ComputedRef } from 'vue'
import type { MathExercise, MathExerciseListKey, MathExerciseListPatch } from './store/interfaces'

import { MathExerciseType } from './enums'
import { useAssignmentEditorStore } from './store'
import { isTypeableAnswer } from '../../lib/assignments/math-practice-schema'

/** The one prop every sub-form takes. */
export interface MathExerciseEditorProps {
  exercise: MathExercise
}

export interface MathExerciseListRow {
  id: string
  /** 1-based. */
  position: number
  /** The item's string fields, `''` where a stored item has none. */
  values: Record<string, string>
  isFirstInOrder: boolean
  isLastInOrder: boolean
}

interface UseMathExerciseItemListOptions {
  exercise: () => MathExercise
  list: () => MathExerciseListKey
  /** Below this the remove control is disabled; the schema reports the shortfall. */
  min: () => number
  /** At this the add control is disabled; the store refuses past it too. */
  max: () => number | undefined
}

interface UseMathExerciseItemListReturn {
  refRows: ComputedRef<MathExerciseListRow[]>
  refCanAdd: ComputedRef<boolean>
  refCanRemove: ComputedRef<boolean>
  addItem: () => void
  removeItem: (itemId: string) => void
  moveItemUp: (itemId: string) => void
  moveItemDown: (itemId: string) => void
  updateItem: (itemId: string, patch: Record<string, string>) => void
}

const toValues = (item: unknown): Record<string, string> => {
  if (typeof item !== 'object' || item === null) {
    return {}
  }

  return Object.fromEntries(Object.entries(item)
    .map(([key, value]) => [key, typeof value === 'string' ? value : '']))
}

/** The list of whichever key the sub-form names; `[]` while the exercise carries none. */
export const useMathExerciseItemList = (
  { exercise, list, min, max }: UseMathExerciseItemListOptions,
): UseMathExerciseItemListReturn => {
  const store = useAssignmentEditorStore()

  const refItems = computed<unknown[]>(() => {
    const record: Record<string, unknown> | undefined = exercise()
    const items: unknown = record?.[list()]

    return Array.isArray(items) ? items : []
  })

  const refRows = computed<MathExerciseListRow[]>(() =>
    refItems.value.map((item, index) => {
      const values = toValues(item)

      return {
        id: values.id ?? '',
        position: index + 1,
        values,
        isFirstInOrder: index === 0,
        isLastInOrder: index === refItems.value.length - 1,
      }
    }))

  const refCanAdd = computed<boolean>(() => {
    const limit = max()

    return limit === undefined || refItems.value.length < limit
  })

  const refCanRemove = computed<boolean>(() => refItems.value.length > min())

  return {
    refRows,
    refCanAdd,
    refCanRemove,
    addItem: (): void => { store.addExerciseListItem(exercise().id, list()) },
    removeItem: (itemId: string): void => { store.removeExerciseListItem(exercise().id, list(), itemId) },
    moveItemUp: (itemId: string): void => { store.moveExerciseListItem(exercise().id, list(), itemId, -1) },
    moveItemDown: (itemId: string): void => { store.moveExerciseListItem(exercise().id, list(), itemId, 1) },
    updateItem: (itemId: string, patch: Record<string, string>): void => {
      store.updateExerciseListItem(exercise().id, list(), itemId, patch as MathExerciseListPatch<MathExerciseListKey>)
    },
  }
}

interface UseMathExerciseAnswerKeyReturn {
  /** The keyed option or tick id; `''` while none is marked. */
  refCorrectId: ComputedRef<string>
  setCorrectId: (itemId: string) => void
}

/** Multiple choice keys `correctOptionId`, number line `correctTickId` — one radio either way. */
export const useMathExerciseAnswerKey = (exercise: () => MathExercise): UseMathExerciseAnswerKeyReturn => {
  const store = useAssignmentEditorStore()

  const refCorrectId = computed<string>(() => {
    const current = exercise()

    if (current?.type === MathExerciseType.MultipleChoice) {
      return typeof current.correctOptionId === 'string' ? current.correctOptionId : ''
    }
    if (current?.type === MathExerciseType.NumberLine) {
      return typeof current.correctTickId === 'string' ? current.correctTickId : ''
    }

    return ''
  })

  return {
    refCorrectId,
    setCorrectId: (itemId: string): void => { store.setExerciseCorrectId(exercise().id, itemId) },
  }
}

interface UseMathExerciseNumericReturn {
  refAnswer: ComputedRef<string>
  /** Said while typing, not only on validate: an untypeable answer is a dead end for the kid. */
  refIsUntypeable: ComputedRef<boolean>
  setAnswer: (value: string) => void
}

export const useMathExerciseNumeric = (exercise: () => MathExercise): UseMathExerciseNumericReturn => {
  const store = useAssignmentEditorStore()

  const refAnswer = computed<string>(() => {
    const current = exercise()

    return current?.type === MathExerciseType.Numeric && typeof current.answer === 'string' ? current.answer : ''
  })

  const refIsUntypeable = computed<boolean>(() =>
    refAnswer.value.trim() !== '' && !isTypeableAnswer(refAnswer.value))

  return {
    refAnswer,
    refIsUntypeable,
    setAnswer: (value: string): void => { store.setExerciseAnswer(exercise().id, value) },
  }
}

/** For a multi-step row's answer: the same keypad rule, per step. */
export const isUntypeableAnswer = (value: string): boolean =>
  value.trim() !== '' && !isTypeableAnswer(value)

export const KEYPAD_HINT = 'Kids answer on a keypad: digits, "-", "." and "/" only — no spaces or letters.'
